import { IWixAPI } from '@wix/yoshi-flow-editor';
import { IFedOpsLogger } from '@wix/native-components-infra/dist/src/types/types';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { configureStore } from '@reduxjs/toolkit';

import { ServicesMap } from '@common/services';
import {
  Status,
  getMemberPosts,
} from '@common/store/member-slice/member-posts-slice';
import { initErrorMonitorMiddleware } from '@common/store/errors-slice';
import { RootState, rootReducer, rootActions } from './root-reducer';

export type ThunkExtra = { services: ServicesMap };
export type AppDispatch = ThunkDispatch<RootState, ThunkExtra, Action>;
export type RootStore = ReturnType<typeof initStore>;

enum Interactions {
  InitPosts = 'init-posts',
  DeletePost = 'delete-post',
}

const MEMBER_POSTS_ORIGIN = 'member_posts_page';

export const initStore = (
  services: ServicesMap,
  errorLogger: (error: Error) => void,
) => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: { services },
        },
        serializableCheck: false,
      }).concat([initErrorMonitorMiddleware(errorLogger)]),
  });

  return store;
};

export const initActions = (
  wixCodeApi: IWixAPI,
  store: RootStore,
  errorLogger: (error: Error) => void,
  biLogger: (biEvent: { evid: number; [key: string]: any }) => void,
  fedopsLogger: IFedOpsLogger,
) => {
  const dispatch: AppDispatch = store.dispatch;

  return {
    // @TODO maybe group loggers into app actions?
    logError: errorLogger,
    logBiEvent: biLogger,
    navigateTo: (
      path: string,
      queryParams: { [key: string]: any } = {
        origin: MEMBER_POSTS_ORIGIN,
      },
    ) => {
      if (wixCodeApi.location.to) {
        const url = new URL(path);

        if (queryParams) {
          for (const key in queryParams) {
            url.searchParams.append(key, queryParams[key]);
          }
        }

        wixCodeApi.location.to(url.toString());
      }
    },
    // App actions
    initAppBase: async ({ language }: { language: string }) => {
      const baseProps = {
        language,
      };

      await Promise.all([
        dispatch(rootActions.setBaseProps(baseProps)),
        dispatch(rootActions.fetchCurrentUser()),
        dispatch(rootActions.fetchTranslations(language)),
      ]);
    },
    initAppData: async ({ viewedUserId }: { viewedUserId: string }) => {
      fedopsLogger.interactionStarted(Interactions.InitPosts);

      await dispatch(
        rootActions.fetchInitialMemberPosts({ userId: viewedUserId }),
      );

      fedopsLogger.interactionEnded(Interactions.InitPosts);
    },
    fetchCurrentUser: async () => {
      await dispatch(rootActions.fetchCurrentUser());
    },
    fetchMemberPosts: async (options: { sort?: string; offset?: number }) => {
      await dispatch(rootActions.fetchMemberPosts(options));
    },
    deleteMemberPost: async (postId: string, isLastItemOnPage: boolean) => {
      fedopsLogger.interactionStarted(Interactions.DeletePost);

      const memberPosts = getMemberPosts(store.getState());

      if (memberPosts.status === Status.READY) {
        const {
          pagination: { currentFilter },
        } = memberPosts;
        let updatedFilter = { ...currentFilter };

        if (isLastItemOnPage) {
          const updatedOffset = currentFilter.offset - currentFilter.size;

          updatedFilter = {
            ...updatedFilter,
            offset: updatedOffset > 0 ? updatedOffset : currentFilter.offset,
          };
        }

        await dispatch(rootActions.deleteMemberPost(postId));
        await dispatch(rootActions.fetchMemberPosts(updatedFilter));

        fedopsLogger.interactionEnded(Interactions.DeletePost);
      }
    },
  };
};
